import Vue from "vue";
import moment from "moment";
import axios from "axios";
import mime from "mime-types";
import { ScheduleLogEntry } from "@/store/modules/types";
import {
  ScheduleFilter,
  CalendarFilter,
  ScheduleState,
  SchedulePatientFilter,
  ScheduleDateFilter,
  ScheduleAppointment,
  FileFilter,
  MessageSummaryReportFilter
} from "./types";
import { DateTimeSpan, ScheduleView } from "./enums";
import { prepareFormQuestions } from "@/services/forms";

const getDefaultFilter = (): ScheduleFilter => {
  return {
    entityId: "",
    patient: {
      firstName: "",
      lastName: "",
      phone: "",
      insurerMemberId: "",
    },
    calendarList: [],
    priorityList: [],
    langList: [],
    reasonList: [],
    date: {
      timeSpan: null,
      from: moment().toDate(),
      to: moment()
        .add(30, "days")
        .toDate(),
    },
    lastReminder: {
      timeSpan: null,
      from: null,
      to: null,
    },
    statusList: [],
    created: {
      timeSpan: null,
      from: null,
      to: null,
    },
    updated: {
      timeSpan: null,
      from: null,
      to: null,
    },
    transportation: null,
    archived: false,
    isOnline:false,
    request:true,
    viewedByPatient:false,
    reminderType:"",
    orderBy: "DATE",
    pageNumber: 1,
    rowsPerPage: 200,
  };
};

const getDefaultState = (): ScheduleState => {
  return {
    view: ScheduleView.List,
    list: {
      filter: getDefaultFilter(),
      showMore: false,
      pageRows: [],
      totalRows: 0,
    },
    calendars: [],
    reasons: [],
    priorities: [],
    status: [],
    statusCount: [],
    reminderCount: [],
    typeStatus: [],
  };
};

const getDateFilterRange = (filter: ScheduleDateFilter) => {
  let from: any = null;
  let to: any = null;
  if (filter.timeSpan) {
    switch (filter.timeSpan) {
      case DateTimeSpan.TODAY:
        from = moment().format("YYYY-MM-DD");
        break;
      case DateTimeSpan.WEEK:
        from = moment()
          .startOf("week")
          .format("YYYY-MM-DD");
        to = moment()
          .endOf('week')
          .format("YYYY-MM-DD");
        break;
      case DateTimeSpan.LAST7:
        from = moment()
          .subtract(7, "day")
          .format("YYYY-MM-DD");
        to = moment().format("YYYY-MM-DD");
        break;
      case DateTimeSpan.MONTH:
        from = moment()
          .startOf("month")
          .format("YYYY-MM-DD");
        to = moment()
          .endOf('month')
          .format("YYYY-MM-DD");
        break;
    }
    filter.from = moment(from).toDate().toString() ;
    filter.to = moment(to).toDate().toString();

    //to = moment().format("YYYY-MM-DD");
  } else {
    from = moment(filter.from).format("YYYY-MM-DD");
    to = moment(filter.to).format("YYYY-MM-DD");
  }
  return { from, to };
};

const filterOn = (filter: any) => {
  if (typeof filter === "object") {
    for (const field in filter) {
      const value = filter[field];
      if (field !== "cache") {
        if (Array.isArray(value)) {
          if (value.length) return true;
        } else if (value) {
          return true;
        }
      }
    }
  } else {
    return filter ? true : false;
  }
  return false;
};

const filesUrl = (path: string): string => {
  let { hostname } = window.location;
  let url = "https://files.";
  if (hostname.includes("localhost") || hostname.includes(".dev.")) {
    url += "dev.";
  } else if (hostname.includes(".qa.")) {
    url += "qa.";
  } else if (hostname.includes(".uat.")) {
    url += "uat.";
  }
  url += "waitingmed.com" + (path ? "/" + path : "");
  return url;
};

export default {
  namespaced: true,
  state: getDefaultState(),
  mutations: {
    SET_VIEW(state: ScheduleState, view: ScheduleView) {
      state.view = view;
    },
    SET_CALENDARS(state: ScheduleState, calendars: Array<any>) {
      state.calendars = calendars;
    },
    SET_CALENDAR_REASONS(state: ScheduleState, reasons: Array<any>) {
      state.reasons = reasons;
    },
    SET_PRIORITIES(state: ScheduleState, priorities: Array<any>) {
      state.priorities = priorities;
    },
    SET_STATUS(state: ScheduleState, status: Array<any>) {
      state.status = status;
    },
    SET_TYPE_STATUS(state: ScheduleState, typeStatus: Array<any>) {
      state.typeStatus = typeStatus;
    },
    // FILTER
    SET_PATIENT_FILTER(state: ScheduleState, filter: SchedulePatientFilter) {
      state.list.filter.patient = filter;
    },
    SET_CALENDAR_FILTER(state: ScheduleState, filter: any) {
      state.list.filter.calendarList = filter;
    },
    SET_PRIORITY_FILTER(state: ScheduleState, filter: any) {
      state.list.filter.priorityList = filter;
    },
    SET_REASON_FILTER(state: ScheduleState, filter: any) {
      state.list.filter.reasonList = filter;
    },
    SET_LANG_FILTER(state: ScheduleState, filter: any) {
      state.list.filter.langList = filter;
    },
    SET_DATE_FILTER(state: ScheduleState, filter: ScheduleDateFilter) {
      state.list.filter.date = filter;
    },
    SET_LAST_REMINDER_FILTER(state: ScheduleState, filter: ScheduleDateFilter) {
      state.list.filter.lastReminder = filter;
    },
    SET_STATUS_FILTER(state: ScheduleState, filter: number[]) {
      state.list.filter.statusList = filter;
    },
    SET_CREATED_FILTER(state: ScheduleState, filter: ScheduleDateFilter) {
      state.list.filter.created = filter;
    },
    SET_UPDATED_FILTER(state: ScheduleState, filter: ScheduleDateFilter) {
      state.list.filter.updated = filter;
    },
    SET_ORDER_BY_FILTER(state: ScheduleState, field: string) {
      state.list.filter.orderBy = field;
      state.list.filter.pageNumber = 1;
    },
    RESET_FILTER(state: ScheduleState, entityId: string) {
      if (!entityId) entityId = state.list.filter.entityId;
      state.list.filter = getDefaultFilter();
      state.list.filter.entityId = entityId;
    },
    // LIST
    SET_PAGE_NUMBER(state: ScheduleState, number: number) {
      state.list.filter.pageNumber = number;
    },
    SET_ROWS_PER_PAGE(state: ScheduleState, number: number) {
      state.list.filter.rowsPerPage = number;
    },
    SET_PAGE_DATA(state: ScheduleState, data: any) {
      state.list.pageRows = data.pageRows.map((appt) => {
        appt.checked = false;
        appt.reminder = {
          sent: false,
          error: "",
        };
        return appt;
      });
      state.list.totalRows = data.totalRows;
    },
    SET_STATUS_COUNT(state: ScheduleState, data: any) {
      state.statusCount = data;
    },
    SET_REMINDER_COUNT(state: ScheduleState, data: any) {
      state.reminderCount = data;
    },
    CHECK_PAGE_ROWS(state: ScheduleState) {
      const { pageRows } = state.list;
      for (let i = 0; i < pageRows.length; i++) {
        const appt = pageRows[i];
        if (!appt.archived) appt.checked = true;
      }
    },
    UNCHECK_PAGE_ROWS(state: ScheduleState) {
      const { pageRows } = state.list;
      for (let i = 0; i < pageRows.length; i++) {
        const appt = pageRows[i];
        appt.checked = false;
        appt.reminder.sent = false;
        appt.reminder.error = "";
        appt.updated = false;
        appt.updateError = "";
      }
    },
    //
    RESET_STATE(state: ScheduleState) {
      Object.assign(state, getDefaultState());
    },
  },
  actions: {
    async getApptList({ state, commit }) {
      // SET FORM DATA
      const { filter } = state.list;
      const params = new FormData();
      params.append("entityId", filter.entityId);
      params.append("calendarList", filter.calendarList.join());
      params.append("firstName", filter.patient.firstName);
      params.append("lastName", filter.patient.lastName);
      params.append("insurerMemberId", filter.patient.insurerMemberId);
      params.append("phone", filter.patient.phone);
      // GET DATE FILTER
      const date = getDateFilterRange(filter.date);
      params.append("dateFrom", date.from);
      params.append("dateTo", date.to);
      // GET LAST REMINDER FILTER
      const lastReminder = getDateFilterRange(filter.lastReminder);
      params.append("lastReminderDateFrom", lastReminder.from);
      params.append("lastReminderDateTo", lastReminder.to);

      if(filter.reminderType)params.append("reminderType", filter.reminderType);
      // GET CREATED FILTER
      const created = getDateFilterRange(filter.created);
      params.append("recordDateFrom", created.from);
      params.append("recordDateTo", created.to);
      // GET UPDATED FILTER
      const updated = getDateFilterRange(filter.updated);
      params.append("lastUpdateFrom", updated.from);
      params.append("lastUpdateTo", updated.to);
      //GET TRANSPORTATION FILTER
      if (filter.transportation) params.append("transportation", filter.transportation.toString());
      //
      if (filter.archived != null) params.append("archived", filter.archived.toString());
      //
       if(filter.isOnline)params.append("isOnline", filter.isOnline.toString());
       params.append("request", filter.request.toString());
      //
      if(filter.viewedByPatient)params.append("viewedByPatient", filter.viewedByPatient.toString());
      //
      params.append("priorityList", filter.priorityList.join());
      params.append("reasonList", filter.reasonList.join());
      params.append("langList", filter.langList.map((lang) => {
        return "'" + lang + "'";
      }).join());
      params.append("statusList", filter.statusList.join());
      params.append("orderBy", filter.orderBy);
      params.append("pageNumber", filter.pageNumber.toString());
      params.append("rowsPerPage", filter.rowsPerPage.toString());
      // GET APPT LIST ASYNC
      const { data } = await Vue.axios.post("schedule/wmpc/appt/list", params);
      //
      commit("SET_PAGE_DATA", data);
    },
    async getApptStatusCount({ state, commit }) {
      // SET FORM DATA
      const { filter } = state.list;
      const params = new FormData();
      params.append("entityId", filter.entityId);
      params.append("calendarList", filter.calendarList.join());
      params.append("firstName", filter.patient.firstName);
      params.append("lastName", filter.patient.lastName);
      params.append("insurerMemberId", filter.patient.insurerMemberId);
      params.append("phone", filter.patient.phone);
      // GET DATE FILTER
      const date = getDateFilterRange(filter.date);
      params.append("dateFrom", date.from);
      params.append("dateTo", date.to);
      // GET LAST REMINDER FILTER
      const lastReminder = getDateFilterRange(filter.lastReminder);
      params.append("lastReminderDateFrom", lastReminder.from);
      params.append("lastReminderDateTo", lastReminder.to);
      // GET CREATED FILTER
      const created = getDateFilterRange(filter.created);
      params.append("recordDateFrom", created.from);
      params.append("recordDateTo", created.to);
      // GET UPDATED FILTER
      const updated = getDateFilterRange(filter.updated);
      params.append("lastUpdateFrom", updated.from);
      params.append("lastUpdateTo", updated.to);
      //GET TRANSPORTATION FILTER
      if (filter.transportation) params.append("transportation", filter.transportation.toString());
      //
      if (filter.archived != null) params.append("archived", filter.archived.toString());
      //
      if(filter.isOnline)params.append("isOnline", filter.isOnline.toString());
     //
     if(filter.viewedByPatient)params.append("viewedByPatient", filter.viewedByPatient.toString());
      //
      params.append("priorityList", filter.priorityList.join());
      params.append("langList", filter.langList.map((lang) => {
        return "'" + lang + "'";
      }).join());
      params.append("statusList", filter.statusList.join());
      // GET APPT LIST ASYNC
      const { data } = await Vue.axios.post("schedule/wmpc/appt/status/count", params);
      //
      commit("SET_STATUS_COUNT", data);
    },
    async getApptReminderCount({ state, commit }) {
      // SET FORM DATA
      const { filter } = state.list;
      const params = new FormData();
      params.append("entityId", filter.entityId);
      params.append("calendarList", filter.calendarList.join());
      params.append("firstName", filter.patient.firstName);
      params.append("lastName", filter.patient.lastName);
      params.append("insurerMemberId", filter.patient.insurerMemberId);
      params.append("phone", filter.patient.phone);
      // GET DATE FILTER
      const date = getDateFilterRange(filter.date);
      params.append("dateFrom", date.from);
      params.append("dateTo", date.to);
      // GET LAST REMINDER FILTER
      const lastReminder = getDateFilterRange(filter.lastReminder);
      params.append("lastReminderDateFrom", lastReminder.from);
      params.append("lastReminderDateTo", lastReminder.to);
      // GET CREATED FILTER
      const created = getDateFilterRange(filter.created);
      params.append("recordDateFrom", created.from);
      params.append("recordDateTo", created.to);
      // GET UPDATED FILTER
      const updated = getDateFilterRange(filter.updated);
      params.append("lastUpdateFrom", updated.from);
      params.append("lastUpdateTo", updated.to);
      //GET TRANSPORTATION FILTER
      if (filter.transportation) params.append("transportation", filter.transportation.toString());
      //
      if (filter.archived != null) params.append("archived", filter.archived.toString());
      //
      //
      if(filter.isOnline)params.append("isOnline", filter.isOnline.toString());
     //
     if(filter.viewedByPatient)params.append("viewedByPatient", filter.viewedByPatient.toString());
     //
      params.append("priorityList", filter.priorityList.join());
      params.append("langList", filter.langList.map((lang) => {
        return "'" + lang + "'";
      }).join());
      params.append("statusList", filter.statusList.join());
      // GET APPT LIST ASYNC
      const { data } = await Vue.axios.post("schedule/wmpc/appt/reminder/count", params);
      //
      commit("SET_REMINDER_COUNT", data);
    },
    async getApptRecord({ commit }, id: number) {
      // SET FORM DATA
      const params = new FormData();
      params.append("id", id.toString());
      // GET APPT ASYNC
      const { data } = await Vue.axios.post(
        "schedule/wmpc/appt/record",
        params
      );
      //
      return data;
    },
    async getApptFileList({ commit }, id: number) {
      // SET FORM DATA
      const params = new FormData();
      params.append("id", id.toString());
      // GET FILES ASYNC
      const { data } = await Vue.axios.post(
        "schedule/wmpc/appt/file/list",
        params
      );
      //
      return data;
    },
    async getCalendarViewEvents({ state, rootState }, dateFilter: ScheduleDateFilter) {
      const { filter } = state.list;
      // SET FORM DATA
      const params = new FormData();
      params.append("entityId", rootState.auth.entityId);
      params.append("dateFrom", moment(dateFilter.from).format("YYYY-MM-DD"));
      params.append("dateTo", moment(dateFilter.to).format("YYYY-MM-DD"));
      params.append("calendarList", filter.calendarList.join());
      params.append("orderBy", "DATE");
      params.append("pageNumber", "1");
      params.append("rowsPerPage", "3000");
      // GET APPT LIST ASYNC
      const { data } = await Vue.axios.post("schedule/wmpc/appt/list", params);
      //
      return data.pageRows.map((appt) => {
        let start = appt.date;
        if (appt.time) {
          start += " " + appt.time;
        }
        return {
          id: appt.id,
          title: appt.patientName,
          start: moment(start).format(),
        };
      });
    },
    async getCalendars({ commit, rootState }) {
      // SET FORM PARAMS
      const params = new FormData();
      params.append("entityId", rootState.auth.entityId);
      //
      const { data } = await Vue.axios.post("schedule/calendar/list", params);
      commit("SET_CALENDARS", data);
    },
    async getCalendarList({ commit, rootState }, filter: CalendarFilter) {
      // SET FORM PARAMS
      const params = new FormData();
      params.append("entityId", rootState.auth.entityId);
      params.append("calendarName", filter.name);
      params.append("pageNumber", filter.pageNumber.toString());
      params.append("rowsPerPage", filter.rowsPerPage.toString());
      //
      const { data } = await Vue.axios.post("schedule/calendar/basic/list", params);
      return data;
    },
    async getCalendarConfig({ commit, rootState }, calendarId: number) {
      // SET FORM PARAMS
      const params = new FormData();
      params.append("calendarId", calendarId.toString());
      // GET REASONS ASYNC
      const { data } = await Vue.axios.post(
        "schedule/calendar/config",
        params
      );
      //
      return data;
    },
    async getCalendarReasons({ commit, rootState }, calendarId: number) {
      // SET FORM PARAMS
      const params = new FormData();
      if(calendarId){
        params.append("calendarId", calendarId.toString());
      }else{
        params.append("entityId", rootState.auth.entityId);
      }
      
      // GET REASONS ASYNC
      const { data } = await Vue.axios.post(
        "schedule/calendar/reason/list",
        params
      );
      //

      if(!calendarId){
        commit("SET_CALENDAR_REASONS", data);
      }

      return data;
    },
    async getCalendarPriorities({ commit, rootState }, calendarId: string) {
      // SET FORM PARAMS
      const params = new FormData();
      params.append("entityId", rootState.auth.entityId);
      params.append("calendarId", calendarId);
      // GET PRIORITIES ASYNC
      const { data } = await Vue.axios.post("schedule/priority/list", params);
      //
      return data;
    },
    async getCalendarDates({ commit, rootState }, calendarId: number) {
      // SET FORM PARAMS
      const params = new FormData();
      params.append("calendarId", calendarId.toString());
      // GET DATES ASYNC
      const { data } = await Vue.axios.post("schedule/calendar/dates", params);
      //
      return data;
    },
    async getCalendarFullDates({ commit, rootState }, calendarId: number) {
      // SET FORM PARAMS
      const params = new FormData();
      params.append("calendarId", calendarId.toString());
      // GET DATES ASYNC
      const { data } = await Vue.axios.post(
        "schedule/calendar/dates/full",
        params
      );
      //
      return data;
    },
    async getCalendarTimes({ commit, rootState }, { calendarId, date }) {
      // SET FORM PARAMS
      const params = new FormData();
      params.append("calendarId", calendarId);
      params.append("date", date);
      // GET TIMES ASYNC
      const { data } = await Vue.axios.post("schedule/calendar/times", params);
      //
      return data;
    },
    async saveCalendarConfig({ commit, rootState }, calendar: object) {
      // SET FORM PARAMS
      const params = new FormData();

      params.append("calendarId", calendar["calendarId"].toString());
      params.append("config", JSON.stringify(calendar["config"]));
      params.append("deleteConfig", JSON.stringify(calendar["deleteConfig"]));
      // GET REASONS ASYNC
      const { data } = await Vue.axios.post(
        "schedule/settings/calendar/config/save",
        params
      );
      //
      return data;
    },
    async getEntityInsurers({ commit, rootState }, entityId: number) {
      // SET FORM PARAMS
      const params = new FormData();
      params.append("entityId", entityId.toString());
      // GET REASONS ASYNC
      const { data } = await Vue.axios.post("entity/insurer/list", params);
      //
      return data;
    },
    async getStatus({ commit, rootState }) {
      // SET FORM PARAMS
      const params = new FormData();
      params.append("entityId", rootState.auth.entityId);
      // GET STATUS LIST ASYNC
      const { data } = await Vue.axios.post("schedule/status/list", params);
      //
      commit("SET_STATUS", data);
    },
    async getTypeStatus({ commit, state }) {
      if (state.typeStatus.length) return;
      // GET TYPE STATUS LIST ASYNC
      const { data } = await Vue.axios.post("schedule/type/status/list");
      //
      commit("SET_TYPE_STATUS", data);
    },
    async getLog({ commit }, id: number) {
      // SET FORM DATA
      const params = new FormData();
      params.append("id", String(id));
      // SET GAP LOG ASYNC
      const { data } = await Vue.axios.post("schedule/appt/log", params);
      //
      return data;
    },
    async getApptPreReqList({ commit }, { calendarId, apptTypeId, apptId }) {
      // SET FORM DATA
      const params = new FormData();
      if (calendarId) params.append("calendarId", calendarId);
      if (apptTypeId) params.append("apptTypeId", apptTypeId);
      if (apptId) params.append("apptId", apptId);
      // GET PREREQS ASYNC
      const { data } = await Vue.axios.post(
        "schedule/appt/prereq/list",
        params
      );
      //
      return data;
    },
    async getApptFormQuestions({ commit }, { id, formId }) {
      // SET FORM DATA
      const params = new FormData();
      params.append("id", id);
      params.append("formId", formId);
      // GET FORM QUESTIONS ASYNC
      const { data } = await Vue.axios.post(
        "schedule/appt/form/question/list",
        params
      );
      //
      return prepareFormQuestions(data);
    },
    async getPatientGapList({ rootState }, id: number) {
      // SET FORM DATA
      const params = new FormData();
      params.append("patientId", id.toString());
      // GET PATIENT GAPS ASYNC
      const { data } = await Vue.axios.post("patient/gap/list", params);
      //
      return data;
    },
    async getLinks({ commit }, id: number) {
      // SET FORM DATA
      const params = new FormData();
      params.append("id", id.toString());
      // GET LINKS ASYNC
      const { data } = await Vue.axios.post("schedule/appt/link/list", params);
      //
      return data;
    },
    async getPriorities({ commit, rootState }, calendarId: string) {
      // SET FORM PARAMS
      const params = new FormData();
      params.append("entityId", rootState.auth.entityId);
      if (calendarId) params.append("calendarId", calendarId);
      // GET PRIORITIES ASYNC
      const { data } = await Vue.axios.post("schedule/priority/list", params);
      //
      commit("SET_PRIORITIES", data);
    },
    async saveAppt({ rootState }, appt: ScheduleAppointment) {
      // SET FORM DATA
      const params = new FormData();
      if (appt.id) params.append("id", appt.id.toString());
      if (appt.main.patient.id)
        params.append("patientId", appt.main.patient.id.toString());
      if (appt.main.time) params.append("time", appt.main.time);
      params.append("calendarId", appt.main.calendarId);
      params.append("reasonId", appt.main.reasonId);
      params.append("priorityId", appt.main.priorityId);
      params.append("typeId", appt.main.typeId);
      params.append("date", appt.main.date!);
      params.append("sessionId", appt.main.sessionId);
      params.append("firstName", appt.main.patient.firstName);
      params.append(
        "lastName",
        appt.main.patient.lastName +
        (appt.main.patient.lastName2 ? " " + appt.main.patient.lastName2 : "")
      );

      if (appt.main.patient.birth.date) {
        params.append("birthdate", appt.main.patient.birth.date);
      }

      params.append("phone", appt.main.patient.phone);

      if (appt.main.patient.email)
        params.append("email", appt.main.patient.email.toString());

      params.append("insurerId", appt.main.patient.insurer.id);
      params.append("insurerMemberId", appt.main.patient.insurer.memberId);
      params.append("serviceDescription", appt.service.description);
      params.append("serviceAddress", JSON.stringify(appt.service.address));
      params.append("prereqs", JSON.stringify(appt.prereqs.list));
      params.append("fileList", appt.files.map((f) => f.key).join());
      params.append("EMR_PATIENT_ID", appt.main.patient.EMR_ID);
      params.append("comments", appt.comment);
      params.append("lang", appt.main.lang);
      params.append("statusId", appt.main.statusId);
      if (appt.main.transportation) params.append("transportation", String(appt.main.transportation));
      params.append("archived", String(appt.main.archived));
      // SAVE APPT ASYNC
      const { data } = await Vue.axios.post("schedule/appt/save", params);
      //
      return data;
    },
    async saveApptFile({ rootState }, { id, key }) {
      // SET FORM DATA
      const params = new FormData();
      params.append("id", id);
      params.append("key", key);
      // SAVE APPT ASYNC
      const { data } = await Vue.axios.post("schedule/appt/file/save", params);
      //
      return data;
    },
    async saveStatus({ commit }, { id, statusId, comment }) {
      // SET FORM DATA
      const params = new FormData();
      params.append("id", id);
      params.append("statusId", statusId);
      params.append("comment", comment ? comment : "");
      // SAVE STATUS ASYNC
      const { data } = await Vue.axios.post("schedule/appt/status", params);
      //
      return data;
    },
    async saveLog({ commit }, entry: ScheduleLogEntry) {
      // SET FORM DATA
      const params = new FormData();
      params.append("apptId", entry.apptId.toString());
      params.append("typeId", entry.typeId);
      if (entry.statusId) params.append("statusId", entry.statusId!.toString());
      if (entry.comment) params.append("comment", entry.comment);
      // SAVE LOG ASYNC
      const { data } = await Vue.axios.post("schedule/appt/log/save", params);
      //
      return data;
    },
    async saveLink({ commit }, { apptId, id, url, description }) {
      // SET FORM DATA
      const params = new FormData();
      params.append("apptId", apptId);
      params.append("id", id);
      params.append("url", url);
      params.append("description", description);
      // SAVE LINK ASYNC
      const { data } = await Vue.axios.post("schedule/appt/link/save", params);
      //
      return data;
    },
    async saveApptFormAnswers({ commit }, { id, formId, answers }) {
      // SET FORM DATA
      const params = new FormData();
      params.append("id", id);
      params.append("formId", formId);
      params.append("list", JSON.stringify(answers));
      // SAVE APPT ANSWERS ASYNC
      const { data } = await Vue.axios.post(
        "schedule/appt/form/answer/save/list",
        params
      );
      //
      return data;
    },
    async markLogRead({ commit }, id: string) {
      // SET FORM DATA
      const params = new FormData();
      params.append("id", id);
      // MARK LOG READ ASYNC
      const { data } = await Vue.axios.post("schedule/appt/log/read", params);
      //
      return data;
    },
    async archiveApptFile({ rootState }, { id, fileId }) {
      // SET FORM DATA
      const params = new FormData();
      params.append("id", id);
      params.append("fileId", fileId);
      // SAVE APPT ASYNC
      const { data } = await Vue.axios.post(
        "schedule/appt/file/archive",
        params
      );
      //
      return data;
    },
    async archiveLink({ rootState }, { apptId, id }) {
      // SET FORM DATA
      const params = new FormData();
      params.append("apptId", apptId);
      params.append("id", id);
      // ARCHIVE LINK ASYNC
      const { data } = await Vue.axios.post(
        "schedule/appt/link/archive",
        params
      );
      //
      return data;
    },
    async uploadFile({ rootState }, file: File) {
      // SET FORM PARAMS
      const params = new FormData();
      params.append("files[0]", file);

      // UPLOAD FILE ASYNC
      const { data } = await axios.post(filesUrl("ul"), params);
      //
      return data;
    },
    async uploadFiles({ rootState }, files: File[]) {
      // SET FORM PARAMS
      const params = new FormData();
      for (var x = 0; x < files.length; x++) {
        params.append("files[" + x + "]", files[x]);
      }
      // UPLOAD FILE ASYNC
      const { data } = await axios.post(filesUrl("ul"), params);
      //
      return data;
    },
    async downloadFile({ rootState }, key: string) {
      window.location.href = filesUrl("dl/wm/" + key);
    },
    async viewFile({ rootState }, file) {
      const fileURL = filesUrl("dl/wm/" + file.key);
      const fileType = mime.lookup(file.name);
      const { data } = await axios.get(fileURL, {
        responseType: "blob",
      });
      const blob = new Blob([data], { type: fileType });
      const blobURL = URL.createObjectURL(blob);
      console.log(fileType);
      window.open(blobURL);
    },
    async sendApptCall({ commit }, id: number) {
      // SET FORM DATA
      const params = new FormData();
      params.append("apptId", id.toString());
      // SEND CALL ASYNC
      const { data } = await Vue.axios.post(
        "schedule/appt/reminder/call",
        params
      );
      //
      return data;
    },
    async sendApptSMS({ commit }, id: number) {
      // SET FORM DATA
      const params = new FormData();
      params.append("apptId", id.toString());
      // SEND SMS ASYNC
      const { data } = await Vue.axios.post(
        "schedule/appt/reminder/sms",
        params
      );
      //
      return data;
    },
    async sendApptEMAIL({ commit }, id: number) {
      // SET FORM DATA
      const params = new FormData();
      params.append("apptId", id.toString());
      // SEND SMS ASYNC
      const { data } = await Vue.axios.post(
        "schedule/appt/reminder/email",
        params
      );
      //
      return data;
    },
    async sendOpenNotificationSMS({ commit }, {id, message,lang}) {
      // SET FORM DATA
      const params = new FormData();
      params.append("apptId", id.toString());
      params.append("message", message.toString());
      params.append("lang", lang.toString());
      // SEND SMS ASYNC
      const { data } = await Vue.axios.post(
        "schedule/appt/notification/sms",
        params
      );
      //
      return data;
    },
    async sendOpenNotificationCall({ commit }, {id, message,lang}) {
      // SET FORM DATA
      const params = new FormData();
      params.append("apptId", id.toString());
      params.append("message", message.toString());
      params.append("lang", lang.toString());
      // SEND SMS ASYNC
      const { data } = await Vue.axios.post(
        "schedule/appt/notification/call",
        params
      );
      //
      return data;
    },
    async archiveAppt({ commit }, id: number) {
      // SET FORM DATA
      const params = new FormData();
      params.append("apptId", id.toString());
      // SEND SMS ASYNC
      const { data } = await Vue.axios.post(
        "schedule/appt/archive",
        params
      );
      //
      return data;
    },
    async changeLang({ commit }, { id, apptList, lang }) {
      // SET FORM DATA
      const params = new FormData();
      if (id) params.append("apptId", id.toString());
      if (apptList) params.append("apptList", apptList.toString());
      if (lang) params.append("lang", lang.toString());
      // SEND SMS ASYNC
      const { data } = await Vue.axios.post(
        "schedule/appt/lang",
        params
      );
      //
      return data;
    },
    async getFileList({ rootState }, filter: FileFilter) {
      // SET FORM DATA
      const params = new FormData();
      const entityId: string = rootState.auth.entityId;
      if (entityId) {
        params.append('entityId', entityId);
      }
      params.append('name', filter.name);
      params.append('pageNumber', String(filter.pageNumber));
      params.append('rowsPerPage', String(filter.rowsPerPage));
      // GET FILES ASYNC
      const { data } = await Vue.axios.post('schedule/file/list', params)
      //
      return data;
    },
    async saveFile({ rootState }, { fileKey, comments }) {
      // SET FORM DATA
      const params = new FormData();
      const entityId: string = rootState.auth.entityId;
      if (entityId) {
        params.append('entityId', entityId);
      }

      if (comments) {
        params.append('comments', comments);
      }
      params.append('fileKeyList', fileKey);

      // GET FILES ASYNC
      const { data } = await Vue.axios.post('schedule/file/save', params)
      //
      return data;
    },
    async getMessageSummaryReport({ rootState }, filter: MessageSummaryReportFilter) {
      // SET FORM DATA
      const params = new FormData();
      const entityId: string = rootState.auth.entityId;
      if (filter.entity) {
        params.append('entityId', filter.entity.toString());
      }
      //params.append('type', String(filter.type));
      if(filter.type)params.append('type', String(filter.type));
      params.append('dateFrom', moment(filter.date.from).format('YYYY-MM-DD').toString());
      params.append('dateTo', moment(filter.date.to).format('YYYY-MM-DD').toString());

      // GET FILES ASYNC
      const { data } = await Vue.axios.post('schedule/report/summary/message', params)
      //
      return data;
    },
    async getCrMessageSummaryReport({ rootState }, filter: MessageSummaryReportFilter) {
      // SET FORM DATA
      const params = new FormData();
      const entityId: string = rootState.auth.entityId;
      if (filter.entity) {
        params.append('entityId', filter.entity.toString());
      }
      //params.append('type', String(filter.type));
      if(filter.type)params.append('type', String(filter.type));
      params.append('dateFrom', moment(filter.date.from).format('YYYY-MM-DD').toString());
      params.append('dateTo', moment(filter.date.to).format('YYYY-MM-DD').toString());

      // GET FILES ASYNC
      const { data } = await Vue.axios.post('schedule/report/cr/summary/message', params)
      //
      return data;
    },
  },
  getters: {
    patientFilterOn: (state: ScheduleState) => {
      return filterOn(state.list.filter.patient);
    },
    checkedRows: (state: ScheduleState) => {
      return state.list.pageRows.filter((item) => item.checked);
    },
    notRemindedRows: (state: ScheduleState) => {
      return state.list.pageRows.filter(
        (item) => item.checked && !item.reminder.sent && !item.reminder.error
      );
    },
  },
};
